import React, { useState, useEffect, useMemo } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { Container, Text } from "@pixi/react";
import { TextStyle } from "pixi.js";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { Settings } from "../../../../settings";
import { PixiSettings } from "../../../../pixiSettings";
import {
  FONT_CONFIG,
  USER_INTERFACE_BUTTON_CONFIG,
  TEXT_STYLE_CONFIG,
} from "../../../../pixiSettings";

// Extend dayjs with timezone plugins
dayjs.extend(utc);
dayjs.extend(timezone);

const Clock = ({ timeZone = "Europe/Belgrade", toolbar }) => {
  const {
    device,
    orientation,
    getResponsiveConfig,
    uiSymbolScale,
    isMobileSmall,
  } = useDimensions();
  const [time, setTime] = useState(dayjs());

  useEffect(() => {
    const timer = setInterval(() => {
      setTime(dayjs());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Format the time string — shows game name and time
  const formattedTime = useMemo(
    () => `${Settings.APP.GAME_NAME} | ${time.tz(timeZone).format("HH:mm")}`,
    [time, timeZone],
  );

  // Get button dimensions using the same system as other secondary buttons
  const buttonConfig = useMemo(
    () => getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG, true),
    [getResponsiveConfig],
  );

  // Unified clock configuration (font multiplier, x, y positions)
  const clockConfig = useMemo(() => {
    let fontMultiplier = 1.0;
    let x = 0;
    let y = 0;

    if (isMobileSmall) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        fontMultiplier = 1.0;
        x = toolbar.width / 2;
        y = buttonConfig.height * 0.3;
      } else {
        fontMultiplier = 0.875;
        x = buttonConfig.width * 2.75;
        y = buttonConfig.height * 0.6;
      }
    } else if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        fontMultiplier = 1.0;
        x = toolbar.width / 2;
        y = buttonConfig.height * 0.225;
      } else {
        fontMultiplier = 0.9;
        x = buttonConfig.width * 2.75;
        y = buttonConfig.height * 0.7;
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        fontMultiplier = 1.55;
        x = toolbar.width / 2;
        y = buttonConfig.height * 0.3;
      } else {
        fontMultiplier = 1.1;
        x = toolbar.width / 2;
        y = buttonConfig.height * 0.4;
      }
    } else {
      // Desktop
      fontMultiplier = 1.0;
      x = toolbar.width - buttonConfig.width * 4.25;
      y = buttonConfig.height * 0.785;
    }

    return { fontMultiplier, x, y };
  }, [
    device,
    orientation,
    isMobileSmall,
    toolbar.width,
    buttonConfig.width,
    buttonConfig.height,
  ]);

  // Calculate font size — snap to even for clean DPR rasterization
  const baseFontSize = 28;
  const FONT_SIZE = useMemo(() => {
    const responsiveFontSize = baseFontSize * clockConfig.fontMultiplier;
    const raw = responsiveFontSize * uiSymbolScale;
    return Math.round(raw / 2) * 2;
  }, [clockConfig.fontMultiplier, uiSymbolScale]);

  // Text style
  const clockStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: FONT_SIZE,
        fontWeight: "600",
        fill: 0xffffff,
        align: "center",
        resolution: window.devicePixelRatio || 1,
        ...TEXT_STYLE_CONFIG.OUTLINE,
      }),
    [FONT_SIZE],
  );

  // Clock position
  const clockPosition = useMemo(
    () => ({
      x: Math.round(clockConfig.x),
      y: Math.round(clockConfig.y),
    }),
    [clockConfig.x, clockConfig.y],
  );

  return (
    <Container
      name="secondary_clock"
      x={clockPosition.x}
      y={clockPosition.y}
      zIndex={3}
    >
      <Text
        text={formattedTime}
        style={clockStyle}
        anchor={0.5}
        roundPixels={true}
        x={0}
        y={0}
        accessible={true}
        accessibleTitle={formattedTime}
      />
    </Container>
  );
};

export default Clock;
